import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Mail, 
  RefreshCw, 
  Check, 
  X,
  AlertCircle,
  Clock,
  Eye,
  EyeOff,
  ChevronDown,
  Building2,
  Briefcase, 
  Users,
  Bot,
} from 'lucide-react';
import axiosInstance from '../../lib/api/axiosInstance';
import { Button } from '../ui/button';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter,
} from '../ui/card';
import { Input } from '../ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { useToast } from '../ui/use-toast';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';

// Types pour notre composant
interface ContactSuggestion {
  name: string;
  email: string;
  occurrence: number;
  lastSeen: Date;
  subjects: string[];
  suggestedType: 'CLIENT' | 'PARTNER' | 'INTERNAL' | 'SYSTEM' | 'UNKNOWN';
}

interface ExistingContact {
  email: string;
  contactType: string;
  clientId?: number;
  partnerId?: number;
  entityName: string;
}

interface Client {
  id: number;
  name: string;
  type: string;
  status: string;
}

interface Partner {
  id: number;
  name: string;
  type: string;
  status: string;
}

// Interface pour les nouveaux contacts à ajouter
interface NewContactData {
  name: string;
  email: string;
  contactType: 'CLIENT' | 'PARTNER' | 'INTERNAL' | 'SYSTEM' | 'UNKNOWN';
  role?: string | null;
  clientId?: number | null;
  partnerId?: number | null;
  phone?: string | null;
  notes?: string | null;
}

export const ContactDetection: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [suggestions, setSuggestions] = useState<ContactSuggestion[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [existingContacts, setExistingContacts] = useState<ExistingContact[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pendingContacts, setPendingContacts] = useState<Map<string, NewContactData>>(new Map());
  const [processedEmails, setProcessedEmails] = useState<Set<string>>(new Set());
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [showExistingContacts, setShowExistingContacts] = useState(false);
  
  // Charger les suggestions de contacts au chargement
  useEffect(() => {
    loadContactSuggestions();
  }, []);
  
  // Fonction pour charger les suggestions de contacts
  const loadContactSuggestions = async () => {
    setLoading(true);
    setError(null);
    setRefreshing(true);
    
    try {
      const response = await axiosInstance.get<{
        newContacts: ContactSuggestion[],
        clients: Client[],
        partners: Partner[],
        existingContacts: ExistingContact[]
      }>('/contacts/detect', {
        params: { maxResults: 50 }
      });
      
      setSuggestions(response.data.newContacts || []);
      setClients(response.data.clients || []);
      setPartners(response.data.partners || []);
      setExistingContacts(response.data.existingContacts || []);
      setLastRefreshed(new Date());
    } catch (error: any) {
      console.error('Error fetching contact suggestions:', error);
      setError(error.response?.data?.error || t('contact.detection.errorLoading', 'Error loading contact suggestions'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Fonction pour préparer l'ajout d'un contact
  const prepareSaveContact = (suggestion: ContactSuggestion, entityId: number, type: 'CLIENT' | 'PARTNER') => {
    // Initialiser les données du nouveau contact
    const newContactData: NewContactData = {
      name: suggestion.name,
      email: suggestion.email,
      contactType: type,
      role: 'Contact',  // Rôle par défaut
      clientId: type === 'CLIENT' ? entityId : null,
      partnerId: type === 'PARTNER' ? entityId : null,
      notes: suggestion.subjects.length > 0 
        ? `Détecté via les emails. Sujets récents: ${suggestion.subjects.join(', ')}`
        : null
    };
    
    // Ajouter le contact à la liste des contacts en attente
    const updatedPendingContacts = new Map(pendingContacts);
    updatedPendingContacts.set(suggestion.email, newContactData);
    setPendingContacts(updatedPendingContacts);
  };
  
  // Fonction pour ignorer un contact
  const ignoreContact = (email: string) => {
    // Marquer l'email comme traité
    setProcessedEmails(new Set(processedEmails).add(email));
    
    // Supprimer de la liste en attente s'il y est
    if (pendingContacts.has(email)) {
      const updatedPendingContacts = new Map(pendingContacts);
      updatedPendingContacts.delete(email);
      setPendingContacts(updatedPendingContacts);
    }
  };
  
  // Fonction pour mettre à jour les données d'un contact en attente
  const updatePendingContact = (email: string, field: keyof NewContactData, value: any) => {
    if (!pendingContacts.has(email)) return;
    
    const updatedPendingContacts = new Map(pendingContacts);
    const contactData = updatedPendingContacts.get(email);
    if (contactData) {
      updatedPendingContacts.set(email, { ...contactData, [field]: value });
      setPendingContacts(updatedPendingContacts);
    }
  };
  
  // Fonction pour enregistrer un contact
  const saveContact = async (email: string) => {
    const contactData = pendingContacts.get(email);
    if (!contactData) return;
    
    try {
      // Envoyer les données au serveur
      await axiosInstance.post('/contacts', contactData);
      
      // Afficher une notification de succès
      toast({
        title: t('contact.added', 'Contact ajouté'),
        description: t('contact.addedDescription', 'Le contact a été ajouté avec succès'),
      });
      
      // Mettre à jour les listes
      setProcessedEmails(new Set(processedEmails).add(email));
      
      const updatedPendingContacts = new Map(pendingContacts);
      updatedPendingContacts.delete(email);
      setPendingContacts(updatedPendingContacts);
    } catch (error: any) {
      console.error('Error saving contact:', error);
      toast({
        variant: 'destructive',
        title: t('contact.addError', 'Erreur'),
        description: error.response?.data?.error || t('contact.addErrorDescription', 'Une erreur est survenue lors de l\'ajout du contact'),
      });
    }
  };
  
  // Filtrer les suggestions pour ne montrer que celles qui n'ont pas été traitées
  const filteredSuggestions = suggestions.filter(
    suggestion => !processedEmails.has(suggestion.email)
  );
  

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <div className="space-y-1">
            <CardTitle className="text-xl flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              {t('contact.detection.title', 'Détection de contacts')}
            </CardTitle>
            {lastRefreshed && (
              <CardDescription className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {t('common.lastUpdated')}: {lastRefreshed.toLocaleTimeString()}
              </CardDescription>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowExistingContacts(!showExistingContacts)}
              className="h-9"
            >
              {showExistingContacts ? (
                <>
                  <EyeOff className="mr-2 h-4 w-4" />
                  {t('contact.detection.hideExisting', 'Masquer existants')}
                </>
              ) : (
                <>
                  <Eye className="mr-2 h-4 w-4" />
                  {t('contact.detection.showExisting', 'Voir existants')}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadContactSuggestions}
              disabled={loading || refreshing}
              className="h-9"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t('contact.detection.refresh', 'Actualiser')}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common.error')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {loading && !refreshing ? (
          <div className="space-y-2">
            {[1, 2].map(i => (
              <div key={i} className="flex justify-between items-center p-3 border rounded-md">
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-5 w-1/4" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
                <div className="flex space-x-2">
                  <Skeleton className="h-9 w-24" />
                  <Skeleton className="h-9 w-9" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {showExistingContacts && existingContacts.length > 0 && (
              <Accordion type="single" collapsible className="mb-4">
                <AccordionItem value="existing-contacts">
                  <AccordionTrigger>
                    {t('contact.detection.existingContacts', 'Contacts existants')} ({existingContacts.length})
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 mt-2 max-h-72 overflow-y-auto p-1">
                      {existingContacts.map((contact, index) => (
                        <div key={index} className="flex justify-between items-center p-3 border rounded-md bg-muted/30">
                          <div>
                            <p className="font-medium text-sm">{contact.email}</p>
                            <p className="text-xs text-muted-foreground">
                              {contact.entityName} - {t(`contact.type.${contact.contactType.toLowerCase()}`, contact.contactType)}
                            </p>
                          </div>
                          <Badge variant="outline">
                            {t('contact.detection.alreadyInDatabase', 'Déjà en base')}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
            
            {pendingContacts.size > 0 && (
              <div className="bg-muted/50 p-4 rounded-md mb-4">
                <h3 className="font-medium mb-2">{t('contact.detection.pendingContacts', 'Contacts en attente d\'ajout')}</h3>
                <div className="space-y-3">
                  {Array.from(pendingContacts.entries()).map(([email, data]) => (
                    <div key={email} className="bg-card p-3 border rounded-md">
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <p className="font-medium">{data.email}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">{t('contact.name')}</label>
                              <Input 
                                value={data.name} 
                                onChange={(e) => updatePendingContact(email, 'name', e.target.value)}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">{t('contact.role')}</label>
                              <Input 
                                value={data.role || ''} 
                                onChange={(e) => updatePendingContact(email, 'role', e.target.value)}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">{t('contact.type')}</label>
                              <Select 
                                value={data.contactType} 
                                onValueChange={(value: any) => updatePendingContact(email, 'contactType', value)}
                              >
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue placeholder={t('contact.selectType', 'Sélectionner un type')} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="CLIENT">{t('contact.type.client')}</SelectItem>
                                  <SelectItem value="PARTNER">{t('contact.type.partner')}</SelectItem>
                                  <SelectItem value="INTERNAL">{t('contact.type.internal')}</SelectItem>
                                  <SelectItem value="SYSTEM">{t('contact.type.system')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {data.contactType === 'CLIENT' && (
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">{t('contact.client')}</label>
                                <Select 
                                  value={data.clientId?.toString() || ''} 
                                  onValueChange={(value) => updatePendingContact(email, 'clientId', parseInt(value))}
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue placeholder={t('contact.selectClient', 'Sélectionner un client')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {clients.map(client => (
                                      <SelectItem key={client.id} value={client.id.toString()}>
                                        {client.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            
                            {data.contactType === 'PARTNER' && (
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">{t('contact.partner')}</label>
                                <Select 
                                  value={data.partnerId?.toString() || ''} 
                                  onValueChange={(value) => updatePendingContact(email, 'partnerId', parseInt(value))}
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue placeholder={t('contact.selectPartner', 'Sélectionner un partenaire')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {partners.map(partner => (
                                      <SelectItem key={partner.id} value={partner.id.toString()}>
                                        {partner.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => saveContact(email)}
                          >
                            <Check className="mr-2 h-4 w-4" />
                            {t('contact.detection.save', 'Enregistrer')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = new Map(pendingContacts);
                              updated.delete(email);
                              setPendingContacts(updated);
                            }}
                          >
                            <X className="mr-2 h-4 w-4" />
                            {t('contact.detection.cancel', 'Annuler')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {filteredSuggestions.length > 0 ? (
              <div className="space-y-3">
                <h3 className="font-medium text-sm text-muted-foreground">
                  {t('contact.detection.potentialContacts', 'Contacts potentiels')} ({filteredSuggestions.length})
                </h3>
                {filteredSuggestions.map((suggestion) => (
                  <div key={suggestion.email} className="flex justify-between items-center p-4 border rounded-md hover:bg-muted/30 transition-colors">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{suggestion.name || suggestion.email}</p>
                        <Badge variant="secondary" className="text-xs">
                          {suggestion.occurrence} {t('contact.detection.occurrences', 'occurrences')}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {t(`contact.type.${suggestion.suggestedType.toLowerCase()}`, suggestion.suggestedType)}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{suggestion.email}</p>
                      {suggestion.subjects.length > 0 && (
                        <div className="mt-1 text-xs text-muted-foreground line-clamp-1">
                          {t('contact.detection.latestSubjects', 'Sujets récents')}:&nbsp;
                          {suggestion.subjects.join(', ')}
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline">
                            {t('contact.detection.addContact', 'Ajouter')}
                            <ChevronDown className="ml-2 h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-56">
                          <DropdownMenuLabel>{t('contact.detection.selectEntity', 'Sélectionner une entité')}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuGroup>
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger>
                                <Building2 className="mr-2 h-4 w-4" />
                                <span>{t('contact.type.client')}</span>
                              </DropdownMenuSubTrigger>
                              <DropdownMenuPortal>
                                <DropdownMenuSubContent>
                                  <ScrollArea className="h-56 w-48">
                                    {clients.map(client => (
                                      <DropdownMenuItem 
                                        key={client.id}
                                        onClick={() => prepareSaveContact(suggestion, client.id, 'CLIENT')}
                                      >
                                        {client.name}
                                      </DropdownMenuItem>
                                    ))}
                                  </ScrollArea>
                                </DropdownMenuSubContent>
                              </DropdownMenuPortal>
                            </DropdownMenuSub>
                            
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger>
                                <Briefcase className="mr-2 h-4 w-4" />
                                <span>{t('contact.type.partner')}</span>
                              </DropdownMenuSubTrigger>
                              <DropdownMenuPortal>
                                <DropdownMenuSubContent>
                                  <ScrollArea className="h-56 w-48">
                                    {partners.map(partner => (
                                      <DropdownMenuItem 
                                        key={partner.id}
                                        onClick={() => prepareSaveContact(suggestion, partner.id, 'PARTNER')}
                                      >
                                        {partner.name}
                                      </DropdownMenuItem>
                                    ))}
                                  </ScrollArea>
                                </DropdownMenuSubContent>
                              </DropdownMenuPortal>
                            </DropdownMenuSub>
                            
                            {suggestion.suggestedType === 'INTERNAL' && (
                              <DropdownMenuItem
                                onClick={() => {
                                  const newContactData: NewContactData = {
                                    name: suggestion.name,
                                    email: suggestion.email,
                                    contactType: 'INTERNAL',
                                    role: 'Staff'
                                  };
                                  
                                  const updated = new Map(pendingContacts);
                                  updated.set(suggestion.email, newContactData);
                                  setPendingContacts(updated);
                                }}
                              >
                                <Users className="mr-2 h-4 w-4" />
                                <span>{t('contact.type.internal')}</span>
                              </DropdownMenuItem>
                            )}
                            
                            {suggestion.suggestedType === 'SYSTEM' && (
                              <DropdownMenuItem
                                onClick={() => {
                                  const newContactData: NewContactData = {
                                    name: suggestion.name,
                                    email: suggestion.email,
                                    contactType: 'SYSTEM'
                                  };
                                  
                                  const updated = new Map(pendingContacts);
                                  updated.set(suggestion.email, newContactData);
                                  setPendingContacts(updated);
                                }}
                              >
                                <Bot className="mr-2 h-4 w-4" />
                                <span>{t('contact.type.system')}</span>
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => ignoreContact(suggestion.email)}
                        className="text-muted-foreground"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              !pendingContacts.size && (
                <div className="text-center py-6 text-muted-foreground">
                  <Mail className="mx-auto h-8 w-8 mb-2 opacity-50" />
                  <p>{t('contact.detection.noSuggestions', 'Aucune suggestion de contact trouvée')}</p>
                  <p className="text-sm mt-1">
                    {t('contact.detection.refreshToFind', 'Actualisez pour trouver des contacts potentiels dans vos emails')}
                  </p>
                </div>
              )
            )}
          </>
        )}
      </CardContent>
      
      <CardFooter className="flex justify-between border-t pt-4">
        <p className="text-xs text-muted-foreground">
          {t('contact.detection.disclaimer', 'Les contacts sont extraits de vos emails récents')}
        </p>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {
              setProcessedEmails(new Set());
              setPendingContacts(new Map());
              loadContactSuggestions();
            }}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('contact.detection.reset', 'Réinitialiser')}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};
